import React, { useCallback } from 'react';
import { Alert, Input, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTheme } from '../../../context/ThemeContext';
import { useGoogleMapsLoader } from './useGoogleMapsLoader';
import { useAddressSearch } from './useAddressSearch';
import { useAddressForm } from './useAddressForm';
import { formatPhoneNumber, getPredictionText } from './utils';
import AddressFields from './AddressFields';
import {
  FormWrapper,
  FieldGroup,
  InputWrapper,
  PredictionsDropdown,
  PredictionItem,
  PredictionMainText,
  PredictionSecondaryText,
} from './styles';
import type { GoogleMapsAddressInputProps } from './types';

const { Text } = Typography;

const GoogleMapsAddressInput: React.FC<GoogleMapsAddressInputProps> = ({ currentStore, setCurrentStore, addressErrors }) => {
  const { isDarkMode } = useTheme();
  const { isLoaded, isLoadingScript } = useGoogleMapsLoader();

  const { control, handleAddressSelect, handleFieldChange } = useAddressForm(currentStore, setCurrentStore);

  const handlePhoneNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFieldChange('phoneNumber', formatPhoneNumber(e.target.value));
  }, [handleFieldChange]);

  const {
    searchValue,
    predictions,
    showPredictions,
    isLoadingPredictions,
    predictionsRef,
    handleInputChange,
    handleClearAddress,
    handlePredictionClick,
    handleInputFocus,
  } = useAddressSearch(isLoaded, currentStore, setCurrentStore, handleAddressSelect);

  if (isLoadingScript) {
    return (
      <Alert
        message="Loading Google Maps..."
        description="Setting up address autocomplete..."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  return (
    <FormWrapper>
      <FieldGroup>
        <InputWrapper>
          <Input
            placeholder="Start typing an address..."
            value={searchValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            suffix={
              <CloseOutlined
                onClick={handleClearAddress}
                style={{ cursor: 'pointer', color: '#999', visibility: searchValue ? 'visible' : 'hidden' }}
              />
            }
          />

          {showPredictions && (predictions.length > 0 || isLoadingPredictions) && (
            <PredictionsDropdown ref={predictionsRef} $isDark={isDarkMode}>
              {isLoadingPredictions ? (
                <div style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>Loading suggestions...</Text>
                </div>
              ) : (
                predictions.map((prediction, index) => {
                  const { mainText, secondaryText } = getPredictionText(prediction);
                  return (
                    <PredictionItem
                      key={prediction.placePrediction?.placeId || prediction.place_id || index}
                      $isDark={isDarkMode}
                      onClick={() => handlePredictionClick(prediction)}
                    >
                      <PredictionMainText>{mainText}</PredictionMainText>
                      {secondaryText && <PredictionSecondaryText>{secondaryText}</PredictionSecondaryText>}
                    </PredictionItem>
                  );
                })
              )}
            </PredictionsDropdown>
          )}
        </InputWrapper>
      </FieldGroup>

      <AddressFields
        control={control}
        isDarkMode={isDarkMode}
        onFieldChange={handleFieldChange}
        onPhoneChange={handlePhoneNumberChange}
        errors={addressErrors}
      />

      {!isLoaded && !isLoadingScript && (
        <Alert
          message="Google Maps autocomplete is not available. You can still enter all address details manually above."
          type="warning"
          showIcon
        />
      )}
    </FormWrapper>
  );
};

export default GoogleMapsAddressInput;
